import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Card, CardBody, Spinner} from "reactstrap";
import Select from "react-select";

import EmbedShell from "./EmbedShell";
import {contestoEmbed} from "./embedSession";
import {Utils} from "custom";
import {LeadAPI, ClassRoomTrialAPI, ClassRoomAPI} from "api";

import "moment/locale/it";
import "moment/locale/es";

const moment = require("moment");

/**
 * LE PAROLE DELLA PAGINA, nelle due lingue in cui lavora chi la apre.
 *
 * ⚠️ Stanno qui e non nei file di lingua del gestionale perche' il gestionale v1 e' solo in
 * italiano: questa e' l'unica pagina che i consulenti spagnoli vedono, dentro ATOM. La lingua
 * la dice il backend (`/auth/permission` → `language`): quella dell'utente, o quella
 * dell'istanza se non ne ha mai scelta una.
 */
const TESTI = {
    it: {
        prova: "Lezione di prova",
        classe: "Classe",
        provaNessuna: "da assegnare",
        classeNessuna: "nessun posto riservato",
        provaAssegnata: "Lezione di prova assegnata",
        provaScegli: "Lezioni di prova in programma",
        provaVuota: "Non ci sono lezioni di prova in programma. Le crea l'amministrazione di CLTV.",
        provaSoloConsulente: "La lezione di prova la assegna il consulente che segue il lead: da amministratore qui la vedi soltanto.",
        provaGiaCliente: "Questo contatto su CLTV è già un cliente: la lezione di prova è per chi non ha ancora comprato.",
        provaBloccata: "La presenza è già stata segnata: la lezione non si toglie più.",
        con: "con",
        iscritti: (n) => n === 1 ? "1 iscritto" : n + " iscritti",
        assegna: "Assegna",
        sposta: "Sposta qui",
        togli: "Togli",
        questa: "assegnata",
        inProgramma: "in programma",
        presente: "presente",
        nonPresente: "non risulta presente",
        fattoProva: (quando) => "Lezione di prova assegnata: " + quando + ".",
        fattoProvaTolta: "Lezione di prova tolta.",
        classeRiservata: "Posto riservato",
        classeScegli: "Classi in programmazione",
        classeVuota: "Non ci sono classi in programmazione per questo livello.",
        classeNessunaInProgramma: "Non ci sono classi in programmazione.",
        tuttiLivelli: "Tutti i livelli",
        livello: "Livello",
        docente: "docente",
        posti: (liberi) => liberi === 1 ? "1 posto libero" : liberi + " posti liberi",
        completa: "completa",
        riserva: "Riserva il posto",
        riservato: "riservato",
        turnisti: "Turnisti",
        fissoOre: (ora) => "Fisso · ore " + ora,
        fattoClasse: (dove) => "Posto riservato: " + dove + ".",
        fattoClasseTolta: "Posto in classe liberato.",
        erroreAzione: "Non è stato possibile salvare. Riprova fra un momento.",
        riprova: "Riprova",
        senzaLeadTitolo: "Questa pagina si apre dalla scheda di un lead",
        senzaLeadTesto: "Apri un lead su ATOM e scegli la scheda «Assegna a Classe»: qui compariranno le lezioni di prova e le classi per quella persona.",
        senzaTelefonoTitolo: "Al lead manca un telefono valido",
        senzaTelefonoTesto: "Su CLTV il contatto si riconosce dal telefono, scritto con il prefisso internazionale (per esempio +39 333 1234567). Correggilo sulla scheda del lead in ATOM e riapri questa pagina.",
        nonTuoTitolo: "Su CLTV questo contatto non è tuo",
        nonTuoTesto: "Esiste già su CLTV con lo stesso telefono o la stessa email, ma lo segue un altro consulente (o non è assegnato a nessuno): da qui non puoi gestirlo. Chiedi a un amministratore di CLTV di assegnartelo, poi riapri questa pagina.",
        genericoTitolo: "Non riesco a leggere il lead su CLTV",
        genericoTesto: "Riprova fra un momento. Se continua, segnalalo: il contatto su ATOM è a posto, è questa pagina che non riesce a caricarsi."
    },
    es: {
        prova: "Clase de prueba",
        classe: "Clase",
        provaNessuna: "sin asignar",
        classeNessuna: "ninguna plaza reservada",
        provaAssegnata: "Clase de prueba asignada",
        provaScegli: "Clases de prueba programadas",
        provaVuota: "No hay clases de prueba programadas. Las crea la administración de CLTV.",
        provaSoloConsulente: "La clase de prueba la asigna el asesor que lleva el lead: como administrador aquí solo puedes verla.",
        provaGiaCliente: "Este contacto ya es cliente en CLTV: la clase de prueba es para quien todavía no ha comprado.",
        provaBloccata: "La asistencia ya está registrada: la clase ya no se puede quitar.",
        con: "con",
        iscritti: (n) => n === 1 ? "1 inscrito" : n + " inscritos",
        assegna: "Asignar",
        sposta: "Mover aquí",
        togli: "Quitar",
        questa: "asignada",
        inProgramma: "programada",
        presente: "presente",
        nonPresente: "no consta como presente",
        fattoProva: (quando) => "Clase de prueba asignada: " + quando + ".",
        fattoProvaTolta: "Clase de prueba quitada.",
        classeRiservata: "Plaza reservada",
        classeScegli: "Clases en programación",
        classeVuota: "No hay clases en programación para este nivel.",
        classeNessunaInProgramma: "No hay clases en programación.",
        tuttiLivelli: "Todos los niveles",
        livello: "Nivel",
        docente: "docente",
        posti: (liberi) => liberi === 1 ? "1 plaza libre" : liberi + " plazas libres",
        completa: "completa",
        riserva: "Reservar plaza",
        riservato: "reservada",
        turnisti: "Turnos",
        fissoOre: (ora) => "Fijo · " + ora + " h",
        fattoClasse: (dove) => "Plaza reservada: " + dove + ".",
        fattoClasseTolta: "Plaza liberada.",
        erroreAzione: "No se ha podido guardar. Inténtalo de nuevo en un momento.",
        riprova: "Reintentar",
        senzaLeadTitolo: "Esta página se abre desde la ficha de un lead",
        senzaLeadTesto: "Abre un lead en ATOM y elige la pestaña «Assegna a Classe»: aquí aparecerán las clases de prueba y las clases para esa persona.",
        senzaTelefonoTitolo: "Al lead le falta un teléfono válido",
        senzaTelefonoTesto: "En CLTV el contacto se reconoce por el teléfono, escrito con el prefijo internacional (por ejemplo +34 612 345 678). Corrígelo en la ficha del lead en ATOM y vuelve a abrir esta página.",
        nonTuoTitolo: "En CLTV este contacto no es tuyo",
        nonTuoTesto: "Ya existe en CLTV con el mismo teléfono o el mismo email, pero lo lleva otro asesor (o no está asignado a nadie): desde aquí no puedes gestionarlo. Pide a un administrador de CLTV que te lo asigne y vuelve a abrir esta página.",
        genericoTitolo: "No consigo leer el lead en CLTV",
        genericoTesto: "Inténtalo de nuevo en un momento. Si sigue igual, avisa: el contacto en ATOM está bien, es esta página la que no carga."
    }
};

/** «FISSO ORE 18» e «TURNISTI» sono le parole del backend: qui diventano leggibili, e nella lingua giusta. */
function nomeFascia(t, slot) {
    const testo = String(slot || "");
    if (/^TURNISTI$/i.test(testo)) return t.turnisti;
    const ora = testo.match(/(\d{1,2})\s*$/);
    return ora ? t.fissoOre(String(Number(ora[1]))) : testo;
}

/** «Lunedì 12 ottobre»: maiuscola solo all'inizio, come si scrive (in spagnolo come in italiano). */
function giornoPerEsteso(lingua, data) {
    const testo = moment(data).locale(lingua).format("dddd D MMMM");
    return testo.charAt(0).toUpperCase() + testo.slice(1);
}

/** «lun 12 ott · 18:00», nella lingua di chi guarda. */
function quandoBreve(lingua, data) {
    const m = moment(data).locale(lingua);
    return m.format("ddd D MMM") + " · " + m.format("HH:mm");
}

/**
 * Un avviso che occupa tutta la pagina: non c'e' niente da fare qui finche' non si sistema altrove.
 * Grigio e non rosso quando non e' un guasto (la pagina aperta dal menu, senza un lead).
 */
const Fermo = ({icona, tono, titolo, testo, onRiprova, riprova}) => (
    <Card className="mb-0">
        <CardBody className="text-center py-5">
            <div className="avatar-sm mx-auto mb-3">
                <div className={"avatar-title rounded-circle fs-3 bg-soft-" + tono + " text-" + tono}>
                    <i className={icona}></i>
                </div>
            </div>
            <h5 className="mb-2">{titolo}</h5>
            <p className="text-muted mx-auto mb-0" style={{maxWidth: 520}}>{testo}</p>
            {onRiprova ? (
                <button type="button" className="btn btn-sm btn-soft-secondary mt-3" onClick={onRiprova}>
                    <i className="ri-refresh-line align-bottom me-1"></i>{riprova}
                </button>
            ) : null}
        </CardBody>
    </Card>
);

/**
 * IL CONTENUTO — monta SOLO quando EmbedShell ha gia' scambiato il gettone.
 *
 * 🔴 NON e' un dettaglio stilistico: deve stare DENTRO `<EmbedShell>...</EmbedShell>` come
 * figlio vero, non come componente che RESTITUISCE `<EmbedShell>`. `EmbedShell.render()`
 * mette `this.props.children` nell'albero solo quando `fase === "pronta"` — un componente
 * passato cosi' non monta (e non fa partire i suoi effetti) prima di allora. Un componente
 * che invece RESTITUISCE `<EmbedShell>` monta subito, appena il router lo incontra: il suo
 * `useEffect` partirebbe prima che il gettone sia stato scambiato, leggendo un contesto e un
 * gettone embed che ancora non ci sono — la stessa trappola per cui esiste questo commento.
 */
const AssegnaClasseContenuto = () => {
    const lingua = (Utils.getUserData()?.language === "es") ? "es" : "it";
    const t = TESTI[lingua];
    const ruolo = Utils.getUserData()?.role;

    const [fase, setFase] = useState("carico");     // carico | pronto | fermo
    const [fermo, setFermo] = useState(null);       // quale avviso a tutta pagina
    const [lead, setLead] = useState(null);
    const [prove, setProve] = useState([]);
    const [classi, setClassi] = useState([]);
    const [livello, setLivello] = useState(null);   // filtro delle classi; null = tutti
    const [scheda, setScheda] = useState("prova");  // prova | classe
    const [occupato, setOccupato] = useState(null); // la riga su cui si sta salvando
    const [esito, setEsito] = useState(null);       // {tono, testo}: cosa e' appena successo

    /** Rilegge quello che un'azione puo' aver cambiato: il lead, e i conteggi di prove e classi. */
    const rileggi = useCallback(async (id) => {
        const [leadRes, proveRes, classiRes] = await Promise.all([
            LeadAPI.detail(id),
            ClassRoomTrialAPI.list().catch(() => []),
            ClassRoomAPI.listConsulent().catch(() => [])
        ]);
        setLead(leadRes);
        setProve(proveRes || []);
        setClassi(classiRes || []);
        return leadRes;
    }, []);

    const carica = useCallback(() => {
        const contesto = contestoEmbed();
        setFase("carico");
        setFermo(null);

        /* La pagina aperta dal menu di ATOM, o da un indirizzo salvato: non c'e' un lead per cui
           lavorare. Non e' un errore di nessuno, e si dice cosa fare invece di mostrare un rosso. */
        if (!contesto || contesto.type !== "lead") {
            setFermo("senzaLead");
            setFase("fermo");
            return;
        }
        if (!contesto.phone) {
            setFermo("senzaTelefono");
            setFase("fermo");
            return;
        }

        LeadAPI.match({email: contesto.email, phone: contesto.phone, name: contesto.name})
            .then((res) => rileggi(res.id))
            .then((leadRes) => {
                // Chi ha gia' un posto parte dal suo livello; chi ha gia' la prova ma non la classe, dalla classe.
                if (leadRes.id_group) setLivello(leadRes.id_group.level_id);
                if (leadRes.trial_classroom_id !== null && !leadRes.id_group) setScheda("classe");
                setFase("pronto");
            })
            .catch((e) => {
                /* I due rifiuti che il consulente incontra davvero, detti per quello che sono:
                   · il telefono che CLTV non accetta (`/lead/match` → INVALID_FORM);
                   · il contatto che su CLTV e' di un altro consulente (`/lead/:id` → INVALID_LEAD):
                     prima usciva «Failed! lead is not found!», che faceva credere a un guasto. */
                const stato = e && e.status;
                setFermo(stato === "INVALID_FORM" ? "senzaTelefono" : (stato === "INVALID_LEAD" ? "nonTuo" : "generico"));
                setFase("fermo");
            });
    }, [rileggi]);

    useEffect(() => { carica(); }, [carica]);

    /** Un'azione che scrive: una alla volta, e alla fine si dice com'e' andata. */
    const esegui = async (chiave, azione, fatto) => {
        if (occupato) return;
        setOccupato(chiave);
        setEsito(null);
        try {
            await azione();
            await rileggi(lead.id);
            setEsito({tono: "success", testo: fatto});
        } catch (e) {
            setEsito({tono: "danger", testo: t.erroreAzione});
        }
        setOccupato(null);
    };

    const provaAssegnata = useMemo(() => {
        if (!lead || lead.trial_classroom_id === null) return null;
        return prove.find((p) => p.id === lead.trial_classroom_id)
            // Piu' vecchia di una settimana: l'elenco non la porta piu', ma la data il lead ce l'ha.
            || {id: lead.trial_classroom_id, dateTime: lead.trial_date, teacher: null, partecipants: null, ended: true};
    }, [lead, prove]);

    const proveFuture = useMemo(
        () => prove.filter((p) => !p.ended && moment(p.dateTime).isAfter(moment())),
        [prove]);

    const classeRiservata = useMemo(() => {
        if (!lead || !lead.id_group) return null;
        const classe = classi.find((c) => c.id === lead.id_group.classroom_id);
        if (!classe) return {nome: null, fascia: null};
        const gruppo = classe.groups.find((g) => g.id === lead.id_group.id);
        return {nome: classe.name, fascia: gruppo ? nomeFascia(t, gruppo.slot) : null, classe: classe};
    }, [lead, classi, t]);

    const livelli = useMemo(() => {
        const visti = new Map();
        classi.forEach((c) => { if (!visti.has(c.level.id)) visti.set(c.level.id, c.level.name); });
        return Array.from(visti.entries()).map(([id, nome]) => ({value: id, label: nome}))
            .sort((a, b) => String(a.label).localeCompare(String(b.label), lingua, {numeric: true}));
    }, [classi, lingua]);

    const classiVisibili = useMemo(
        () => classi.filter((c) => livello === null || c.level.id === livello),
        [classi, livello]);

    if (fase === "carico")
        return <div className="text-center py-5"><Spinner color="primary" /></div>;

    if (fase === "fermo") {
        if (fermo === "senzaLead")
            return <Fermo icona="ri-user-search-line" tono="primary" titolo={t.senzaLeadTitolo} testo={t.senzaLeadTesto} />;
        if (fermo === "senzaTelefono")
            return <Fermo icona="ri-phone-line" tono="warning" titolo={t.senzaTelefonoTitolo} testo={t.senzaTelefonoTesto} />;
        if (fermo === "nonTuo")
            return <Fermo icona="ri-user-shared-line" tono="warning" titolo={t.nonTuoTitolo} testo={t.nonTuoTesto} />;
        return <Fermo icona="ri-error-warning-line" tono="danger" titolo={t.genericoTitolo} testo={t.genericoTesto}
                      onRiprova={carica} riprova={t.riprova} />;
    }

    if (!lead) return null;

    const eConsulente = ruolo === "CONSULENT";
    const giaCliente = lead.status === "CONVERTED";
    // La stessa regola del Dettaglio Lead del gestionale: la prova la muove solo il consulente, e mai a un cliente.
    const puoMuovereProva = eConsulente && !giaCliente;
    const provaPassata = provaAssegnata ? moment(provaAssegnata.dateTime).isBefore(moment()) : false;

    const descriviProva = (p) => quandoBreve(lingua, p.dateTime)
        + (p.teacher ? " · " + t.con + " " + [p.teacher.firstname, p.teacher.lastname].filter(Boolean).join(" ") : "");

    const pastiglia = (attiva) => "badge fs-12 fw-medium " + (attiva ? "badge-soft-success" : "bg-light text-muted");

    return (
        <React.Fragment>
            {/* ── CHI È, E A CHE PUNTO È: la prima cosa che serve sapere, senza aprire niente ── */}
            <Card className="mb-3">
                <CardBody className="py-3">
                    <div className="d-flex flex-wrap align-items-center gap-3">
                        <div className="flex-grow-1" style={{minWidth: 220}}>
                            <h5 className="mb-1">{lead.name}</h5>
                            <div className="text-muted fs-13">
                                <i className="ri-phone-line align-bottom me-1"></i>{lead.phone}
                                {lead.email ? <span className="ms-3"><i className="ri-mail-line align-bottom me-1"></i>{lead.email}</span> : null}
                            </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <span className={pastiglia(!!provaAssegnata)}>
                                <i className="ri-calendar-check-line align-bottom me-1"></i>
                                {t.prova}: {provaAssegnata ? quandoBreve(lingua, provaAssegnata.dateTime) : t.provaNessuna}
                            </span>
                            <span className={pastiglia(!!classeRiservata)}>
                                <i className="ri-group-line align-bottom me-1"></i>
                                {t.classe}: {classeRiservata
                                    ? [classeRiservata.nome, classeRiservata.fascia].filter(Boolean).join(" · ") || t.riservato
                                    : t.classeNessuna}
                            </span>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Un <div> e non l'<Alert> di reactstrap: quello porta una dissolvenza che qui non serve. */}
            {esito ? (
                <div className={"alert alert-" + esito.tono + " d-flex align-items-center py-2 mb-3"} role="status">
                    <i className={(esito.tono === "success" ? "ri-check-line" : "ri-error-warning-line") + " fs-16 me-2"}></i>
                    <div className="flex-grow-1">{esito.testo}</div>
                    <button type="button" className="btn-close ms-2" aria-label="×" onClick={() => setEsito(null)}></button>
                </div>
            ) : null}

            <ul className="nav nav-tabs nav-tabs-custom mb-3">
                <li className="nav-item">
                    <button type="button" className={"nav-link" + (scheda === "prova" ? " active" : "")} onClick={() => setScheda("prova")}>
                        <i className="ri-calendar-check-line align-bottom me-1"></i>{t.prova}
                    </button>
                </li>
                <li className="nav-item">
                    <button type="button" className={"nav-link" + (scheda === "classe" ? " active" : "")} onClick={() => setScheda("classe")}>
                        <i className="ri-group-line align-bottom me-1"></i>{t.classe}
                    </button>
                </li>
            </ul>

            {/* ══ LA LEZIONE DI PROVA ═══════════════════════════════════════════════════════════ */}
            {(scheda === "prova") ? <Card className="mb-0"><CardBody>
                {!eConsulente ? <div className="alert alert-info py-2">{t.provaSoloConsulente}</div> : null}
                {giaCliente ? <div className="alert alert-info py-2">{t.provaGiaCliente}</div> : null}

                {provaAssegnata ? (
                    <div className="border border-success rounded p-3 mb-3 bg-soft-success">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <div className="flex-grow-1">
                                <div className="text-muted fs-12 text-uppercase fw-semibold">{t.provaAssegnata}</div>
                                <div className="fs-15 fw-medium">{descriviProva(provaAssegnata)}</div>
                            </div>
                            <span className={"badge fs-12 " + (!provaPassata ? "bg-info" : (lead.trial_check ? "bg-success" : "bg-warning text-dark"))}>
                                {!provaPassata ? t.inProgramma : (lead.trial_check ? t.presente : t.nonPresente)}
                            </span>
                            {/* Con la presenza gia' segnata la prova non si toglie: la stessa regola del gestionale. */}
                            {(puoMuovereProva && !lead.trial_check) ? (
                                <button type="button" className="btn btn-sm btn-soft-danger" disabled={!!occupato}
                                        onClick={() => esegui("togli-prova", () => ClassRoomTrialAPI.insert(lead.id, null), t.fattoProvaTolta)}>
                                    {occupato === "togli-prova" ? <Spinner size="sm" /> : t.togli}
                                </button>
                            ) : null}
                        </div>
                        {(puoMuovereProva && lead.trial_check) ? <div className="text-muted fs-12 mt-2">{t.provaBloccata}</div> : null}
                    </div>
                ) : null}

                <div className="text-muted fs-12 text-uppercase fw-semibold mb-2">{t.provaScegli}</div>

                {proveFuture.length === 0 ? <p className="text-muted mb-0">{t.provaVuota}</p> : (
                    <div className="list-group">
                        {proveFuture.map((p) => {
                            const questa = provaAssegnata && provaAssegnata.id === p.id;
                            const chiave = "prova-" + p.id;
                            return (
                                <div key={p.id} className={"list-group-item d-flex flex-wrap align-items-center gap-2" + (questa ? " bg-soft-success" : "")}>
                                    <div className="flex-grow-1">
                                        <span className="fw-medium">{giornoPerEsteso(lingua, p.dateTime)}</span>
                                        <span className="ms-2 fw-semibold">{moment(p.dateTime).format("HH:mm")}</span>
                                        <div className="text-muted fs-13">
                                            {p.teacher ? [p.teacher.firstname, p.teacher.lastname].filter(Boolean).join(" ") : "—"}
                                            <span className="ms-2">· {t.iscritti((p.partecipants || 0) + (p.partecipants_clients || 0))}</span>
                                        </div>
                                    </div>
                                    {questa ? <span className="badge badge-soft-success fs-12"><i className="ri-check-line align-bottom me-1"></i>{t.questa}</span>
                                        : (puoMuovereProva ? (
                                            <button type="button" className="btn btn-sm btn-primary" disabled={!!occupato}
                                                    onClick={() => esegui(chiave, () => ClassRoomTrialAPI.insert(lead.id, p.id), t.fattoProva(descriviProva(p)))}>
                                                {occupato === chiave ? <Spinner size="sm" /> : (provaAssegnata ? t.sposta : t.assegna)}
                                            </button>
                                        ) : null)}
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardBody></Card> : null}

            {/* ══ LA CLASSE ═════════════════════════════════════════════════════════════════════ */}
            {(scheda === "classe") ? <Card className="mb-0"><CardBody>
                {classeRiservata ? (
                    <div className="border border-success rounded p-3 mb-3 bg-soft-success">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <div className="flex-grow-1">
                                <div className="text-muted fs-12 text-uppercase fw-semibold">{t.classeRiservata}</div>
                                <div className="fs-15 fw-medium">
                                    {[classeRiservata.nome, classeRiservata.fascia].filter(Boolean).join(" · ") || t.riservato}
                                </div>
                            </div>
                            <button type="button" className="btn btn-sm btn-soft-danger" disabled={!!occupato}
                                    onClick={() => esegui("togli-classe", () => LeadAPI.assignGroup({id: lead.id, id_group: null}), t.fattoClasseTolta)}>
                                {occupato === "togli-classe" ? <Spinner size="sm" /> : t.togli}
                            </button>
                        </div>
                    </div>
                ) : null}

                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <div className="text-muted fs-12 text-uppercase fw-semibold flex-grow-1">{t.classeScegli}</div>
                    {livelli.length > 1 ? (
                        <div style={{minWidth: 220}}>
                            <Select
                                aria-label={t.livello}
                                isClearable
                                placeholder={t.tuttiLivelli}
                                value={livelli.find((l) => l.value === livello) || null}
                                options={livelli}
                                onChange={(scelto) => setLivello(scelto ? scelto.value : null)}
                            />
                        </div>
                    ) : null}
                </div>

                {classiVisibili.length === 0
                    ? <p className="text-muted mb-0">{classi.length ? t.classeVuota : t.classeNessunaInProgramma}</p>
                    : classiVisibili.map((c) => (
                        <div key={c.id} className="border rounded mb-2">
                            <div className="d-flex flex-wrap align-items-center gap-2 px-3 py-2 bg-light">
                                <div className="flex-grow-1">
                                    <span className="fw-semibold">{c.name}</span>
                                    <span className="badge badge-soft-primary ms-2">{c.level.name}</span>
                                </div>
                                {c.teacher ? <span className="text-muted fs-13"><i className="ri-user-voice-line align-bottom me-1"></i>{c.teacher.firstname} {c.teacher.lastname}</span> : null}
                            </div>
                            {c.groups.map((g, i) => {
                                const occupati = (g.partecipants || 0) + (g.leads || 0);
                                const liberi = Math.max(0, (g.limit || 0) - occupati);
                                const suo = !!lead.id_group && lead.id_group.classroom_id === c.id && lead.id_group.id === g.id;
                                const chiave = "classe-" + c.id + "-" + (g.id === null ? "turni" : g.id);
                                return (
                                    <div key={i} className={"d-flex flex-wrap align-items-center gap-2 px-3 py-2 border-top" + (suo ? " bg-soft-success" : "")}>
                                        <div className="flex-grow-1">
                                            <span className="fw-medium">{nomeFascia(t, g.slot)}</span>
                                            <span className={"ms-2 fs-13 " + (liberi === 0 ? "text-danger" : (liberi <= 3 ? "text-warning" : "text-success"))}>
                                                {occupati} / {g.limit} · {liberi === 0 ? t.completa : t.posti(liberi)}
                                            </span>
                                        </div>
                                        {suo ? <span className="badge badge-soft-success fs-12"><i className="ri-check-line align-bottom me-1"></i>{t.riservato}</span>
                                            : (
                                                <button type="button" className="btn btn-sm btn-primary" disabled={!!occupato || liberi === 0}
                                                        onClick={() => esegui(chiave,
                                                            () => LeadAPI.assignGroup({id: lead.id, id_group: {id: g.id, classroom_id: c.id}}),
                                                            t.fattoClasse(c.name + " · " + nomeFascia(t, g.slot)))}>
                                                    {occupato === chiave ? <Spinner size="sm" /> : (classeRiservata ? t.sposta : t.riserva)}
                                                </button>
                                            )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
            </CardBody></Card> : null}
        </React.Fragment>
    );
};

/**
 * AZIONE PERSONALIZZATA "Assegna a classe" (ATOM -> CLTV), in modalita' embed.
 *
 * Diversa dalle altre due pagine embed (classi, lezioni-gratuite): quelle sono sola
 * consultazione, questa SCRIVE — sullo stesso lead, con le stesse due strade che il
 * consulente ha gia' oggi dal Dettaglio Lead: la classe reale (`Group`, via `LeadGroup`,
 * capienza vera) e la lezione di prova (`ClassroomTrial`, un campo diretto sul lead, nessuna
 * capienza). Sono rimaste due schede separate qui per lo stesso motivo per cui lo sono in
 * quella pagina: non sono la stessa cosa, e unirle in una lista sola racconterebbe una
 * capienza che la lezione di prova non ha.
 *
 * Il lead non ha un id in comune fra ATOM e CLTV: arriva nel `context` del gettone SSO
 * (email/telefono/nome, vedi handoff.controller.js) e si trova/crea il corrispondente CLTV
 * con `LeadAPI.match` — la STESSA identita' che vede l'operatore, non un id che ATOM non ha.
 *
 * ┌─ COM'È FATTA DALL'11/10/2026, E PERCHÉ ──────────────────────────────────────────────┐
 * │ Dal 10/10 questa è L'UNICA pagina di CLTV dentro la scheda lead di ATOM: da qui si    │
 * │ gestiscono sia le lezioni di prova sia le classi. Prima mostrava una tendina          │
 * │ («Anna | lun 12 ott 20:00 | 2 p.») e un pulsante «MODIFICA», e i rifiuti del backend  │
 * │ così come arrivavano («Failed! lead is not found!»). Adesso:                           │
 * │   · in alto c'è chi è il lead e a che punto è (prova, posto in classe);               │
 * │   · lezioni e classi sono elenchi, un pulsante per riga, e dopo ogni gesto si dice    │
 * │     com'è andata;                                                                      │
 * │   · i tre casi in cui non si può fare niente — pagina aperta senza un lead, telefono  │
 * │     non valido, contatto di un altro consulente — dicono cosa fare;                    │
 * │   · è in italiano o in spagnolo, secondo chi la apre.                                  │
 * │ ⚠️ Chiama solo le rotte che la sessione esterna può chiamare (authJwt.js): niente     │
 * │ `GET /level`, che non serve più — i livelli si leggono dalle classi.                   │
 * └──────────────────────────────────────────────────────────────────────────────────────┘
 */
const EmbedAssegnaClasse = () => {
    document.title = "Assegna a Classe | CLTV";
    return (
        <EmbedShell>
            <AssegnaClasseContenuto />
        </EmbedShell>
    );
};

export default EmbedAssegnaClasse;
